import type { BlockTree } from '@shared/block-renderer';

/**
 * The editorial payloads, as `backend-content` serves them.
 *
 * Field names are **camelCase** on this service, and the pagination meta of an
 * admin list is snake_case — the same split the shelf and taxonomy surfaces
 * already read. Local declarations only because `src/shared/api/generated/` is
 * still empty; codegen replaces them and this file keeps what the surfaces add.
 *
 * **One shape here contradicts the owning service and is carried deliberately.**
 * `content__editorial_posts` stores `body_html TEXT` against an allow-list,
 * while the UI/UX pack, this repo's PRD-TDD, and the shared block subsystem all
 * declare a **block tree**. The tree is what the console holds and what it puts
 * on the wire, for a reason that is not a preference: two of the seven block
 * types the pack's slash menu fixes — `quote` (which renders a `<cite>`) and
 * `note` (an `<aside>`) — use tags `EDITORIAL_BODY_ALLOWED_TAGS` does not carry,
 * so the HTML column cannot hold the drawn contract at all. Mapping them onto
 * tags that *are* allow-listed would be a semantic substitution, and parsing
 * stored HTML back into blocks would be a second parser disagreeing with the
 * server's sanitiser. The conflict is named in this run's report; the single
 * place it is resolved is `./body.ts`.
 */

/**
 * The editorial machine's four states — code-owned, never configuration.
 *
 * There is no `DELETED`. Deleting a post destroys it: the row goes, the console
 * list loses it, and nothing restores it. A fifth state existed to hold a post
 * that had been deleted, and it went out with the restore path it served.
 */
export type EditorialState = 'DRAFT' | 'AWAITING_REVIEW' | 'PUBLISHED' | 'REJECTED';

export interface PostAuthor {
  id: string;
  displayName: string;
}

export interface PostTag {
  id: string;
  name: string;
}

/**
 * A related title. **Attached across every publish state** — a post may
 * be written against a title that is still a draft, which is why that
 * state exists — so the row carries its own state and the console renders it.
 */
export interface RelatedTitle {
  id: string;
  title: string;
  /**
   * `FILM` | `SERIES` — code-owned on the owning service. **Snake-cased here on
   * purpose**: it is the shape the console's other content-item reads already
   * carry, and one entity with two field spellings in one bundle is the drift
   * a shared generated client exists to remove.
   */
  content_type: string;
  production_year?: number | null;
  /** The catalogue machine's state, not the editorial one. */
  publish_state: string;
}

/** One row of the post list. Carries no body — the list is not a reader. */
export interface EditorialPostRow {
  id: string;
  title: string;
  slug: string;
  state: EditorialState;
  author: PostAuthor;
  tags: PostTag[];
  submittedAt: string | null;
  /** ISO-8601 UTC, written by the approval and by nothing else. */
  publishedAt: string | null;
  isFeatured: boolean;
}

export interface EditorialPostDetail extends EditorialPostRow {
  body: BlockTree;
  thumbnailUrl: string | null;
  heroArtworkUrl: string | null;
  relatedItems: RelatedTitle[];
  /**
   * Derived by the owning service from the body's word count over
   * `READING_SPEED_WPM`. Optional because the admin projection does not pin it:
   * where it is absent the byline drops the segment rather than computing a
   * figure against a divisor this bundle would have to hard-code.
   */
  estimatedReadingMinutes: number | null;
  /** Written by the rejection, read by the writer on the post itself. */
  rejectionReason: string | null;
}

export interface EditorialPostListPayload {
  items: EditorialPostRow[];
  page_size: number;
  total: number;
  has_next: boolean;
  /**
   * How many posts each state holds under the current search term — all four
   * states present, an empty one reading `0`.
   *
   * Read, never computed: a count taken over the rows of one page described
   * that page while labelling the whole list, which is what made the review
   * queue's own chip a per-page figure. Optional on the type because a console
   * deployed ahead of the service that serves it must render chips rather than
   * `NaN`.
   */
  state_counts?: Record<string, number>;
}

/** What the post form holds between load and save. */
export interface PostDraft {
  title: string;
  tagIds: string[];
  relatedItemIds: string[];
  body: BlockTree;
}

/**
 * `GET /api/v1/me/permissions` — the RBAC surface every service exposes in the
 * same shape. The console tells the services apart by base URL, so the payload
 * is read exactly as the sibling surfaces already read it.
 */
export interface ContentPermissions {
  role: { name: string; permissions: string[] };
}

/** The grant that makes the two decision actions render at all. */
export const EDITORIAL_REVIEW_PERMISSION = 'editorial_review';

export function holdsReviewerRole(payload: ContentPermissions | undefined): boolean {
  return payload?.role.permissions.includes(EDITORIAL_REVIEW_PERMISSION) ?? false;
}
